// W2Cook Billing Dashboard — Root App

import { useState, useCallback, useEffect } from "react";
import { DEFAULT_SERVICES, type Service } from "./data/services";
import { BUILTIN_CONNECTOR_TEMPLATES, type ConnectorTemplate } from "./connectors/templates";
import { ENVIRONMENTS, ENV_CONFIG, DEFAULT_LIMITS, DEFAULT_NOTIFICATION_CONFIG, type Environment, type BudgetLimits, type NotificationConfig } from "./data/environments";
import { useStore }         from "./hooks/useStore";
import { useLiveData }      from "./hooks/useLiveData";
import { useNotifications } from "./hooks/useNotifications";
import { useAuth }          from "./auth/useAuth";
import { signOut, missingConfigKeys } from "./auth/firebase";
import { tokens as T }      from "./theme/tokens";
import { Btn }              from "./components/ui";
import { LoginScreen }           from "./components/LoginScreen";
import { ConfigErrorScreen }     from "./components/ConfigErrorScreen";
import { DashboardView }         from "./components/DashboardView";
import { ConnectorsView }        from "./components/ConnectorsView";
import { ServiceModal }          from "./components/ServiceModal";
import { ConnectorModal, AddConnectorModal } from "./components/ConnectorModal";
import { SettingsModal }         from "./components/SettingsModal";

type ActiveView = "dashboard" | "connectors";

export default function App() {
  // ── Auth ──────────────────────────────────────────────────────────────────
  const { user, authState } = useAuth();
  // ── Persisted state ───────────────────────────────────────────────────────
  const [services,      setServices     ] = useStore<Service[]>("w2c_services_v4", DEFAULT_SERVICES);
  const [customTpls,    setCustomTpls   ] = useStore<ConnectorTemplate[]>("w2c_custom_connectors", []);
  const [connectorCfgs, setConnectorCfgs] = useStore<Record<string, Record<string, string>>>("w2c_connector_cfgs", {});
  const [limits,        setLimits       ] = useStore<BudgetLimits>("w2c_limits", DEFAULT_LIMITS);
  const [notifications, setNotifications] = useStore<NotificationConfig>("w2c_notifications", DEFAULT_NOTIFICATION_CONFIG);

  const connectorTemplates = [...BUILTIN_CONNECTOR_TEMPLATES, ...customTpls];

  // ── Live polling ──────────────────────────────────────────────────────────
  const { liveData, fetching, lastPolled, fetchOne, fetchAll } = useLiveData(connectorTemplates, connectorCfgs);

  // ── Notifications ─────────────────────────────────────────────────────────
  const { checkSpendAlerts, checkRenewalAlerts } = useNotifications(notifications, limits);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [activeView,      setActiveView     ] = useState<ActiveView>("dashboard");
  const [activeEnv,       setActiveEnv      ] = useState<Environment>("prod");
  const [categoryFilter,  setCategoryFilter ] = useState("All");
  const [editingService,  setEditingService ] = useState<(Partial<Service> & { id: string }) | null>(null);
  const [editingConnector,setEditingConnector] = useState<string | null>(null);
  const [addingConnector, setAddingConnector] = useState(false);
  const [showSettings,    setShowSettings   ] = useState(false);
  const [pulse,           setPulse          ] = useState(false);

  // ── Run alert checks after each poll ─────────────────────────────────────
  const lastPolledTime = lastPolled?.getTime();
  useEffect(() => {
    if (!lastPolledTime) return;
    const active = services.filter(s => s.status === "active");
    const totalMo = active.reduce((s, x) => s + x.monthlyCost, 0);
    const catTotals: Record<string, number> = {};
    active.forEach(s => { catTotals[s.category] = (catTotals[s.category] ?? 0) + s.monthlyCost; });
    checkSpendAlerts(active, totalMo, catTotals);
    checkRenewalAlerts(active);
  }, [lastPolledTime]);

  // ── Service CRUD ──────────────────────────────────────────────────────────
  const saveService = useCallback((updated: Service) => {
    if (!updated.id) updated.id = `svc_${Date.now()}`;
    setServices(prev => {
      const exists = prev.find(s => s.id === updated.id);
      return exists ? prev.map(s => s.id === updated.id ? updated : s) : [...prev, updated];
    });
    setEditingService(null);
  }, [setServices]);

  const deleteService = useCallback((id: string) => {
    if (!confirm("Delete this service?")) return;
    setServices(prev => prev.filter(s => s.id !== id));
    setEditingService(null);
  }, [setServices]);

  const startAddService = () => setEditingService({
    id: "", name: "", category: "Other", environment: activeEnv,
    monthlyCost: 0, status: "active", icon: "🔧", note: "",
    renewsOn: null, apiConnectorId: null, budgetLimit: 0,
  });

  // ── Connector CRUD ────────────────────────────────────────────────────────
  const saveConnectorCfg = useCallback((templateId: string, cfg: Record<string, string>) => {
    setConnectorCfgs(prev => ({ ...prev, [templateId]: cfg }));
    setEditingConnector(null);
    setTimeout(() => fetchOne(templateId), 400);
  }, [setConnectorCfgs, fetchOne]);

  const removeConnectorCfg = useCallback((templateId: string) => {
    if (!confirm("Remove credentials for this connector?")) return;
    setConnectorCfgs(prev => { const n = { ...prev }; delete n[templateId]; return n; });
    setEditingConnector(null);
  }, [setConnectorCfgs]);

  const addCustomConnector = useCallback((tpl: ConnectorTemplate) => {
    setCustomTpls(prev => [...prev.filter(c => c.id !== tpl.id), tpl]);
    setAddingConnector(false);
  }, [setCustomTpls]);

  const removeCustomConnector = useCallback((id: string) => {
    if (!confirm("Remove this connector template?")) return;
    setCustomTpls(prev => prev.filter(c => c.id !== id));
    setServices(prev => prev.map(s => s.apiConnectorId === id ? { ...s, apiConnectorId: null } : s));
  }, [setCustomTpls, setServices]);

  // ── Refresh ───────────────────────────────────────────────────────────────
  const handleRefresh = () => {
    setPulse(true);
    fetchAll().finally(() => setTimeout(() => setPulse(false), 800));
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const configuredCount = Object.keys(connectorCfgs).length;
  const liveCount       = Object.values(liveData).filter(d => d.ok).length;
  const categories      = Array.from(new Set(services.map(s => s.category))).sort();

  // Filter services by environment
  const envServices = services.filter(s => s.environment === activeEnv || s.environment === "all");
  const totalMonthly = envServices.filter(s => s.status === "active").reduce((sum, s) => sum + s.monthlyCost, 0);
  const overLimit = limits.totalMonthly > 0 && totalMonthly > limits.totalMonthly;

  // ── Auth gates ────────────────────────────────────────────────────────────
  if (authState === "loading") {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 13, color: T.textDim }}>Loading…</div>
      </div>
    );
  }

  if (authState === "misconfigured") {
    return <ConfigErrorScreen missingKeys={missingConfigKeys} />;
  }

  if (authState === "signed-out") {
    return <LoginScreen />;
  }

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* ── Topbar ── */}
      <div style={{ borderBottom: `1px solid ${T.border}`, padding: "12px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: T.bg, zIndex: 50 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ background: "#0D2B1F", borderRadius: 8, padding: "4px 8px", display: "flex", alignItems: "center" }}>
              <img src="/dhivins-labs-logo.png" alt="Dhivins Labs" style={{ height: 28, width: "auto", objectFit: "contain" }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: "-0.02em", lineHeight: 1.1 }}>W2Cook</div>
              <div style={{ fontSize: 10, color: T.textDim, letterSpacing: "0.04em" }}>DHIVINS LABS</div>
            </div>
            <span style={{ fontSize: 11, background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 4, padding: "2px 7px", color: T.textMuted, letterSpacing: "0.07em" }}>BILLING</span>
          </div>

          {/* View tabs */}
          <div style={{ display: "flex", gap: 4 }}>
            {(["dashboard", "connectors"] as const).map(v => (
              <button key={v} onClick={() => setActiveView(v)} style={{
                padding: "6px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize",
                background: activeView === v ? T.surfaceAlt : "none",
                border: activeView === v ? `1px solid ${T.border}` : "1px solid transparent",
                color: activeView === v ? T.text : T.textMuted,
              }}>{v}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Environment switcher */}
          <div style={{ display: "flex", gap: 4, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 3 }}>
            {ENVIRONMENTS.map(env => {
              const cfg = ENV_CONFIG[env];
              const isActive = activeEnv === env;
              return (
                <button key={env} onClick={() => setActiveEnv(env)} style={{
                  padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer",
                  fontFamily: "inherit", fontWeight: 600, letterSpacing: "0.06em",
                  background: isActive ? cfg.color : "none",
                  border: "none",
                  color: isActive ? "#fff" : T.textMuted,
                  transition: "all 0.15s",
                }}>{cfg.badge}</button>
              );
            })}
          </div>

          {/* Spend indicator */}
          {overLimit && (
            <div style={{ fontSize: 12, color: T.ember, background: `${T.ember}15`, border: `1px solid ${T.ember}40`, borderRadius: 6, padding: "4px 10px" }}>
              ⚠️ Over limit
            </div>
          )}

          {/* Live indicator */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.textMuted }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: pulse ? T.sage : T.textDim, transition: "background 0.3s" }} />
            {liveCount}/{configuredCount} live
          </div>

          <Btn onClick={handleRefresh}>↻</Btn>
          <Btn variant="subtle" onClick={() => setShowSettings(true)}>⚙️ Settings</Btn>
          <div style={{ width: 1, height: 20, background: T.border }} />
          <div style={{ fontSize: 12, color: T.textDim }}>{user?.email}</div>
          <Btn variant="ghost" onClick={() => signOut()} style={{ fontSize: 12, padding: "6px 12px" }}>Sign out</Btn>
        </div>
      </div>

      {/* ── Environment banner ── */}
      <div style={{
        padding: "6px 24px",
        background: `${ENV_CONFIG[activeEnv].color}15`,
        borderBottom: `1px solid ${ENV_CONFIG[activeEnv].color}30`,
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: ENV_CONFIG[activeEnv].color }} />
        <span style={{ fontSize: 12, color: ENV_CONFIG[activeEnv].color, fontWeight: 600 }}>
          {ENV_CONFIG[activeEnv].label} Environment
        </span>
        <span style={{ fontSize: 12, color: T.textDim }}>
          · ${totalMonthly.toFixed(2)}/mo
          {limits.totalMonthly > 0 && ` of $${limits.totalMonthly} limit`}
          {overLimit && <span style={{ color: T.ember }}> — EXCEEDED</span>}
        </span>
        {notifications.enabled && (
          <span style={{ marginLeft: "auto", fontSize: 11, color: T.sage }}>📧 Alerts on</span>
        )}
      </div>

      {/* ── Main ── */}
      <div style={{ maxWidth: 1020, margin: "0 auto", padding: "24px 20px 60px" }}>
        {activeView === "dashboard" && (
          <DashboardView
            services={envServices}
            connectorTemplates={connectorTemplates}
            connectorCfgs={connectorCfgs}
            liveData={liveData}
            fetching={fetching}
            limits={limits}
            categoryFilter={categoryFilter}
            onCategoryFilter={setCategoryFilter}
            onAddService={startAddService}
            onEditService={setEditingService}
            onFetchOne={fetchOne}
          />
        )}
        {activeView === "connectors" && (
          <ConnectorsView
            services={services}
            connectorTemplates={connectorTemplates}
            connectorCfgs={connectorCfgs}
            liveData={liveData}
            fetching={fetching}
            onConfigure={setEditingConnector}
            onFetchOne={fetchOne}
            onAddConnector={() => setAddingConnector(true)}
            onRemoveCustom={removeCustomConnector}
          />
        )}

        <div style={{ fontSize: 11, color: T.textDim, textAlign: "center", marginTop: 24 }}>
          {services.length} services total · {envServices.length} in {ENV_CONFIG[activeEnv].label}
          {lastPolled && ` · last polled ${lastPolled.toLocaleString()}`}
        </div>
      </div>

      {/* ── Modals ── */}
      {editingService && (
        <ServiceModal
          service={editingService}
          categories={categories}
          connectorTemplates={connectorTemplates}
          configuredConnectors={connectorCfgs}
          onSave={saveService}
          onDelete={deleteService}
          onClose={() => setEditingService(null)}
        />
      )}
      {editingConnector && (() => {
        const tpl = connectorTemplates.find(c => c.id === editingConnector);
        return tpl ? (
          <ConnectorModal
            template={tpl}
            existing={connectorCfgs[editingConnector]}
            onSave={cfg => saveConnectorCfg(editingConnector, cfg)}
            onRemove={() => removeConnectorCfg(editingConnector)}
            onClose={() => setEditingConnector(null)}
          />
        ) : null;
      })()}
      {addingConnector && <AddConnectorModal onSave={addCustomConnector} onClose={() => setAddingConnector(false)} />}
      {showSettings && (
        <SettingsModal
          notifications={notifications}
          limits={limits}
          services={services}
          onSaveNotifications={cfg => { setNotifications(cfg); setShowSettings(false); }}
          onSaveLimits={l => { setLimits(l); setShowSettings(false); }}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}
